import { useTranslation } from "react-i18next";
import type { MySpendByFeature, MySpendByModel } from "@/api";
import { BarRow, PageSection } from "@/components/section/SectionBlocks";
import { Text } from "@/components/Text";
import { formatNumber } from "@/i18n/format";
import { formatCost, isFeature } from "./spend";

const MIN_COST = 0.001;

export function CostByModel({ rows }: Readonly<{ rows: MySpendByModel[] }>) {
  const { t } = useTranslation("settings");
  const shown = rows.filter((r) => r.costUsd >= MIN_COST).sort((a, b) => b.costUsd - a.costUsd);
  const max = Math.max(0, ...shown.map((r) => r.costUsd));
  return (
    <PageSection title={t("usage.byModel")}>
      {shown.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("usage.noData")}
        </Text>
      ) : (
        shown.map((r) => (
          <BarRow
            key={r.modelId}
            label={r.modelId}
            value={formatCost(r.costUsd)}
            fraction={max > 0 ? Math.max(0.04, r.costUsd / max) : 0}
          />
        ))
      )}
    </PageSection>
  );
}

export function CostByFeature({ rows }: Readonly<{ rows: MySpendByFeature[] }>) {
  const { t } = useTranslation("settings");
  const shown = [...rows].sort((a, b) => b.costUsd - a.costUsd);
  const max = Math.max(0, ...shown.map((r) => r.costUsd));
  return (
    <PageSection title={t("usage.byFeature")}>
      {shown.length === 0 ? (
        <Text variant="body-sm" tone="tertiary">
          {t("usage.noData")}
        </Text>
      ) : (
        shown.map((r) => (
          <BarRow
            key={r.feature}
            tone="secondary"
            label={t("usage.featureRow", {
              feature: isFeature(r.feature) ? t(`usage.features.${r.feature}`) : r.feature,
              requests: formatNumber(r.requests),
            })}
            value={formatCost(r.costUsd)}
            fraction={max > 0 ? Math.max(0.04, r.costUsd / max) : 0}
          />
        ))
      )}
    </PageSection>
  );
}
